import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { useLocation } from '@reach/router';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';
import { darken, lighten } from 'polished';
import styled, { useTheme } from 'styled-components';

import { LinkWithQuery } from '@/components/global/LinkWithQuery';
import { ContentMarginLine } from '@/components/v2/GridLines';
import { LayoutGrid, LayoutSubgrid } from '@/components/v2/Page/LayoutGrid';
import { styles } from '@/components/v2/UtilityCss/Text';
import { ChevronDown } from '@/icons/Icons';
import { ReactComponent as IconArrowUpRight } from '@/images/icons/icon-arrow-up-right.svg';
import useMediaQuery from '@/utils/useMediaQuery';

import { leftNav, type NavItem, rightNav } from './config';
import { useHeaderUI } from './HeaderUIProvider';

type NavItemType =
  | Extract<NavItem, { to: string }>
  | Extract<
      NavItem,
      { dropdown: unknown }
    >['dropdown']['lists'][number]['children'][number];

const navItems = [...leftNav, ...rightNav].sort((a, b) => {
  const aVal = 'dropdown' in a ? 0 : 1;
  const bVal = 'dropdown' in b ? 0 : 1;
  return aVal - bVal;
});

const IconChevronRight = styled(ChevronDown)`
  transform: rotate(-90deg);
`;

const HamburgerButton = styled.button`
  --y: 0.175rem;
  --r: 0deg;

  position: relative;
  display: none;
  width: 2.5rem;
  aspect-ratio: 1 / 1;
  margin: auto 0.5rem auto auto;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.palette.fg900};
  border: none;
  border-radius: 999px;
  place-items: center;
  padding: 0;

  &::after,
  &::before {
    content: '';
    height: 1px;
    width: 50%;
    background-color: ${({ theme }) => theme.palette.fg1200};
    grid-area: 1 / -1;
    transform-origin: 50% 50%;
  }

  &::before {
    transform: translateY(var(--y)) rotate(var(--r));
  }

  &::after {
    transform: translateY(calc(-1 * var(--y))) rotate(calc(-1 * var(--r)));
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: grid;
  }
`;

const HamburgerButtonBg = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-color: ${({ theme }) => theme.palette.fg100};
  z-index: -1;

  box-shadow: ${({ theme }) => {
    const lowlight = `0px 0.5px 0 0.75px ${lighten(0.125, theme.palette.fg100)}`;
    return [lowlight].join(',');
  }};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    opacity: 0.98;
    background-image: ${({ theme }) => {
      const stop1 = theme.palette.fg100;
      const stop2 = darken(0.03125, theme.palette.fg100);
      return `radial-gradient(at 0% 0%, ${stop1} 48%, ${stop2})`;
    }};
  }
`;

const HamburgerButtonShadow = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  box-shadow: -6px 8px 12px -4px hsla(0 0% 0% / 0.64);
`;

const MobileNavWrapper = styled(LayoutGrid)`
  position: fixed;
  z-index: 2;
`;

const ScrollableContent = styled(LayoutSubgrid)`
  overflow-y: scroll;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  position: absolute;
  inset: 0;
`;

const Bg = styled(LayoutSubgrid)`
  grid-column: 1 / -1;
  grid-row: 1;
  background-color: ${({ theme }) => theme.palette.fg100};
  background-image: ${({ theme }) =>
    `linear-gradient(210deg, ${theme.palette.fg100} , ${darken(0.025, theme.palette.fg100)})`};
  z-index: -1;

  display: grid;

  &::after {
    content: '';
    grid-row: 1;
    grid-column: 1 / -1;
    background-size: ${({ theme }) => theme.textures.noise.size};
    background-image: ${({ theme }) => theme.textures.noise.img};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    mask-image: linear-gradient(to bottom, rgba(0 0 0 / 1), rgba(0 0 0 / 0.64));
  }
`;

const NavList = styled.ul`
  grid-row: 1;
  padding-inline: 0.6rem;
  padding-top: 2rem;
  padding-bottom: 4rem;
`;

const NavSublistTitle = styled.div`
  padding-left: 0.4rem;
  margin-bottom: 0.25rem;
  font-family: ${({ theme }) => theme.fontFamily.MONO_ALT};
  font-size: ${({ theme }) => theme.fontSize.body.xs};
  font-weight: ${({ theme }) => theme.fontWeight.mono};
  color: ${({ theme }) => theme.palette.fg1100};
  letter-spacing: 0.02rem;
  text-transform: uppercase;
`;

const NavSublist = styled.ul`
  border-bottom: 1px dashed ${({ theme }) => theme.palette.fg600};
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
`;

const NavListItem = styled.li`
  color: ${({ theme }) => theme.palette.fgLoud};
  border: 0.5px solid transparent;
`;

const NavItemTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NavLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSize.body.base};
  line-height: ${({ theme }) => theme.lineHeight.body.base};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  padding: 0.75rem 0.4rem;
  padding-right: 0.7rem;
  border-bottom: none;

  &:hover {
    background-color: transparent;
  }

  & svg {
    color: ${({ theme }) => theme.palette.fg1200};
    display: block;
  }
`;

const FgLoud = styled.span`
  color: ${({ theme }) => theme.palette.fgLoud};
`;

const navItemVariants = {
  base: ({ alpha }: { alpha: string }) => ({
    backgroundColor: alpha
  }),
  tapped: ({ backgroundColor }: { backgroundColor: string }) => ({
    backgroundColor: backgroundColor
  })
};

function NavItemLink({ item }: { item: NavItemType }) {
  const theme = useTheme();
  const { pathname } = useLocation();

  return (
    <NavigationMenu.Item asChild>
      <NavListItem
        key={item.title}
        as={motion.li}
        custom={{
          alpha: theme.palette.fgA100,
          backgroundColor: theme.palette.fgA500,
          borderColor: theme.palette.fgA900
        }}
        initial="base"
        transition={{
          type: 'spring',
          duration: 0.2,
          bounce: 0
        }}
        variants={navItemVariants}
        whileTap="tapped">
        <NavigationMenu.Link active={item.to === pathname} asChild>
          <NavLink
            as={LinkWithQuery}
            isExternal={item.to.includes('https://')}
            to={item.to}>
            <NavItemTitle>
              {item.icon}
              <FgLoud>{item.title}</FgLoud>
            </NavItemTitle>
            {item.to.includes('https://') ? (
              <IconArrowUpRight />
            ) : (
              <IconChevronRight />
            )}
          </NavLink>
        </NavigationMenu.Link>
      </NavListItem>
    </NavigationMenu.Item>
  );
}

function Nav() {
  return (
    <ScrollableContent>
      <NavigationMenu.Root asChild orientation="vertical">
        <LayoutGrid as="nav">
          <NavigationMenu.List asChild>
            <NavList
              animate={{
                scale: 1,
                opacity: 1,
                y: 0,
                transition: { type: 'spring', bounce: 0, duration: 0.3 }
              }}
              as={motion.ul}
              className={styles.base}
              exit={{
                scale: 1.01,
                y: 8,
                opacity: 0,
                transition: { type: 'spring', bounce: 0, duration: 0.2 }
              }}
              initial={{ scale: 1.01, opacity: 0, y: 8 }}>
              {navItems.map(item => {
                if ('dropdown' in item) {
                  const subItems = item.dropdown.lists.flatMap(
                    list => list.children
                  );
                  return (
                    <NavListItem key={item.title}>
                      <NavSublistTitle>{item.title}</NavSublistTitle>
                      <NavSublist>
                        {subItems.map(subItem => (
                          <NavItemLink key={subItem.title} item={subItem} />
                        ))}
                      </NavSublist>
                    </NavListItem>
                  );
                }

                return <NavItemLink key={item.title} item={item} />;
              })}
            </NavList>
          </NavigationMenu.List>
        </LayoutGrid>
      </NavigationMenu.Root>
    </ScrollableContent>
  );
}

function MobileNav() {
  const { navHeight, gridlineHeight } = useHeaderUI();

  return (
    <MotionConfig transition={{ type: 'spring', bounce: 0, duration: 0.3 }}>
      <RemoveScroll removeScrollBar>
        <MobileNavWrapper
          style={{ inset: `${navHeight + gridlineHeight - 2}px 0 0 0` }}>
          <Bg
            animate={{
              opacity: 1,
              transition: { type: 'spring', bounce: 0, duration: 0.1 }
            }}
            as={motion.div}
            exit={{
              opacity: 0,
              transition: { type: 'spring', bounce: 0, duration: 0.05 }
            }}
            initial={{
              opacity: 0
            }}>
            <ContentMarginLine data-minor style={{ gridRow: 1 }} />
            <ContentMarginLine data-minor data-right style={{ gridRow: 1 }} />
          </Bg>
          <LayoutSubgrid data-pile>
            <Nav />
          </LayoutSubgrid>
        </MobileNavWrapper>
      </RemoveScroll>
    </MotionConfig>
  );
}

export function MobileTrigger() {
  const { isMobileOpen, setIsMobileOpen } = useHeaderUI();
  const theme = useTheme();
  const mq = useMediaQuery(theme.mediaQueryString.LARGE);

  return (
    <>
      <HamburgerButton
        animate={isMobileOpen ? 'open' : 'base'}
        aria-label={isMobileOpen ? 'Close menu' : 'Open menu'}
        as={motion.button}
        variants={{
          base: {},
          tapped: {
            scale: 0.9,
            '--y': '0rem',
            '--r': '0deg'
          },
          open: {
            '--y': '0rem',
            '--r': '40deg'
          }
        }}
        whileTap="tapped"
        onClick={() => setIsMobileOpen(v => !v)}>
        <HamburgerButtonShadow
          variants={{
            tapped: {
              scale: 0.92,
              opacity: 0.94
            }
          }}
        />
        <HamburgerButtonBg />
      </HamburgerButton>
      {typeof window === 'undefined'
        ? null
        : createPortal(
            <AnimatePresence>
              {isMobileOpen && mq ? <MobileNav /> : null}
            </AnimatePresence>,
            document.body
          )}
    </>
  );
}
